import { PlusIcon } from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Button } from '@redfoxwp/core-components/components/button';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { LoadingSpinner } from '@redfoxwp/core-components/components/loading-spinner';
import { Sortable } from '@redfoxwp/core-components/components/sortable';
import { __ } from '@wordpress/i18n';
import React, { useState } from 'react';

import { useGlobalTabsContext } from '../../../../contexts';
import { useNotifications } from '../../../../hooks';
import type { GlobalTab, TabModalState } from '../../types';
import { TabItemRenderer } from '../tab-item-renderer';
import { TabModal } from '../tab-modal';
import type { GlobalTabsManagerProps } from './types';

export const GlobalTabsManager: React.FC<GlobalTabsManagerProps> = () => {
  const [modalState, setModalState] = useState<TabModalState>({
    isOpen: false,
    mode: 'add',
  });

  // Use context for all state and operations
  const { tabs, loading: isLoading, isSaving, updateTabs, deleteTab } = useGlobalTabsContext();
  const { showError } = useNotifications();

  const handleAddNewTab = () => {
    setModalState({
      isOpen: true,
      mode: 'add',
    });
  };

  const handleEditTab = (tab: GlobalTab) => {
    // Don't allow editing of WooCommerce default tabs
    if (tab.type === 'woocommerce_default') {
      showError(__('WooCommerce default tabs cannot be edited.', 'redfox-tab-manager'));
      return;
    }
    setModalState({
      isOpen: true,
      mode: 'edit',
      editingTab: tab,
    });
  };

  const handleDeleteTab = async (tabId: string) => {
    await deleteTab(tabId);
  };

  const handleToggleEnabled = (tabId: string, enabled: boolean) => {
    const newTabs = tabs.map((tab: GlobalTab) => (tab.id === tabId ? { ...tab, enabled } : tab));
    updateTabs(newTabs);
  };

  const handleReorder = (reorderedTabs: GlobalTab[]) => {
    // Update priorities based on new order using (10 * index) + 10
    const updatedTabs = reorderedTabs.map((tab, index) => ({
      ...tab,
      priority: 10 * index + 10,
    }));
    updateTabs(updatedTabs);
  };

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      mode: 'add',
    });
  };

  const handleModalSave = (tab: GlobalTab) => {
    let newTabs: GlobalTab[];
    if (modalState.mode === 'edit' && modalState.editingTab) {
      // Update existing tab
      newTabs = tabs.map((existingTab: GlobalTab) =>
        existingTab.id === modalState.editingTab?.id ? tab : existingTab
      );
    } else {
      // Add new tab with priority based on position using (10 * index) + 10
      const newTabWithPriority = {
        ...tab,
        priority: 10 * tabs.length + 10,
      };
      newTabs = [...tabs, newTabWithPriority];
    }

    // Update all priorities based on final order using (10 * index) + 10
    const finalTabs = newTabs.map((t, index) => ({
      ...t,
      priority: 10 * index + 10,
    }));

    updateTabs(finalTabs);
    handleModalClose();
  };

  if (isLoading && tabs.length === 0) {
    return (
      <Card center styleProps={{ padding: '30px' }}>
        <LoadingSpinner />
      </Card>
    );
  }

  return (
    <>
      {/* Overlay Loading Spinner */}
      {isSaving && (
        <LoadingSpinner
          isLoading={true}
          overlay={true}
          overlayMode='absolute'
          centered={true}
          variant='ring'
          size='large'
          showText={true}
          text={__('Saving tabs…', 'redfox-tab-manager')}
        />
      )}

      {/* Main Content */}
      <Flex direction='column' gap='30px'>
        <FlexItem>
          {!Array.isArray(tabs) || tabs.length === 0 ? (
            <Card
              center
              styleProps={{
                border: `1px dashed ${colors.gray[200]}`,
                padding: '30px',
                radius: '5px',
              }}
            >
              <p>
                {__(
                  'No global tabs found. Add your first global tab to get started.',
                  'redfox-tab-manager'
                )}
              </p>
            </Card>
          ) : (
            <Sortable
              items={tabs}
              onReorder={handleReorder}
              renderItem={(tab: GlobalTab, _index: number, isDragging: boolean) => (
                <TabItemRenderer
                  tab={tab}
                  isDragging={isDragging}
                  onEdit={handleEditTab}
                  onDelete={handleDeleteTab}
                  onToggleEnabled={handleToggleEnabled}
                />
              )}
              aria-label={__('Global tabs list', 'redfox-tab-manager')}
              direction='vertical'
            />
          )}
        </FlexItem>
        <FlexItem alignSelf='center'>
          <Button
            onClick={handleAddNewTab}
            disabled={isSaving}
            size='small'
            variant='outline'
            styleProps={{ width: '40px', height: '40px', padding: '0' }}
          >
            <PlusIcon width={20} height={20} />
          </Button>
        </FlexItem>
      </Flex>

      {modalState.isOpen &&
        (() => {
          if (modalState.mode === 'add') {
            return (
              <TabModal
                isOpen={modalState.isOpen}
                mode={modalState.mode}
                onClose={handleModalClose}
                onSave={handleModalSave}
                onError={(_message) => {
                  // Error is handled by the modal component
                }}
              />
            );
          }

          if (modalState.editingTab) {
            return (
              <TabModal
                isOpen={modalState.isOpen}
                mode={modalState.mode}
                editingTab={modalState.editingTab}
                onClose={handleModalClose}
                onSave={handleModalSave}
                onError={(_message) => {
                  // Error is handled by the modal component
                }}
              />
            );
          }

          return null;
        })()}
    </>
  );
};
